import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  createSeason,
  updateSeason,
  fetchLeague,
  fetchLeagueSeason,
  clearLeagueSuccess,
  fetchPointsConfig,
  updatePointsConfig,
  fetchSeasons,
  fetchSeasonSubscribers,
} from '../../store/slices/leagueSlice';
import SeasonForm from '../../components/league/SeasonForm';
import PointsConfigForm from '../../components/league/PointsConfigForm';
import type { CreateSeasonRequest, UpdateSeasonRequest, PointsConfig } from '@sulis-liiga/common';
import { Category } from '@sulis-liiga/common';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

const ManageSeasonPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId, seasonId } = useParams<{ leagueId: string; seasonId: string }>();
  const {
    currentLeague,
    currentSeason,
    pointsConfig,
    seasons,
    seasonSubscribers,
    loading,
    error,
    createSuccess,
    updateSuccess,
  } = useSelector((state: RootState) => state.league);

  const [activeTab, setActiveTab] = useState<'details' | 'points' | 'subscribers'>('details');
  const isEdit = !!seasonId && seasonId !== 'create';
  // Only navigate on a success this page triggered (not a stale flag left over by
  // a cancelled timer on a prior edit page).
  const hasSubmittedRef = useRef(false);

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  // Clear leftover success flags when leaving so a cancelled success timer can't
  // leak a stale flag into the next edit page.
  useEffect(() => {
    return () => {
      dispatch(clearLeagueSuccess());
    };
  }, [dispatch]);

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchSeasons(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  useEffect(() => {
    if (isEdit && leagueId && seasonId && tenantResolved) {
      // Load the season being managed so the form auto-populates with its current
      // details (name, dates, status) instead of showing an empty form. Without
      // this, the form only had data if a previous page happened to load it.
      dispatch(fetchLeagueSeason({ leagueId, seasonId }));
      dispatch(fetchPointsConfig({ leagueId, seasonId }));
    }
  }, [dispatch, leagueId, seasonId, isEdit, tenantResolved]);

  // Fetch season subscribers when the "Subscribed players" tab is active
  useEffect(() => {
    if (activeTab === 'subscribers' && leagueId && seasonId && tenantResolved) {
      dispatch(fetchSeasonSubscribers({ leagueId, seasonId }));
    }
  }, [dispatch, activeTab, leagueId, seasonId, tenantResolved]);

  useEffect(() => {
    if ((createSuccess || updateSuccess) && hasSubmittedRef.current) {
      const timer = setTimeout(() => {
        dispatch(clearLeagueSuccess());
        navigate(`/leagues/${leagueId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [createSuccess, updateSuccess, navigate, dispatch, leagueId]);

  const handleSeasonSubmit = (data: CreateSeasonRequest | UpdateSeasonRequest) => {
    if (!leagueId) return;
    hasSubmittedRef.current = true;

    if (isEdit && seasonId) {
      dispatch(updateSeason({ leagueId, seasonId, seasonData: data as UpdateSeasonRequest }));
    } else {
      dispatch(createSeason({ leagueId, seasonData: data as CreateSeasonRequest }));
    }
  };

  const handlePointsSubmit = (data: PointsConfig) => {
    if (!leagueId || !seasonId) return;
    hasSubmittedRef.current = true;
    dispatch(updatePointsConfig({ leagueId, seasonId, pointsConfig: data }));
  };

  const handleCancel = () => {
    navigate(`/leagues/${leagueId}`);
  };

  if (loading && !currentLeague) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (!currentLeague) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={`/leagues/${leagueId}`}
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <svg
              className="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to League
          </Link>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {isEdit ? 'Manage Season' : 'Create New Season'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{currentLeague.name}</p>
        </div>

        {(createSuccess || updateSuccess) && (
          <div className="mb-6 rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Season {isEdit ? 'updated' : 'created'} successfully
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md bg-danger-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-danger-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {isEdit && (
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`${
                    activeTab === 'details'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Season Details
                </button>
                <button
                  onClick={() => setActiveTab('points')}
                  className={`${
                    activeTab === 'points'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Points Configuration
                </button>
                <button
                  onClick={() => setActiveTab('subscribers')}
                  className={`${
                    activeTab === 'subscribers'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Subscribed Players
                </button>
              </nav>
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {activeTab === 'details' && (
              <SeasonForm
                // Remount when the loaded season arrives so the form
                // re-initialises with its data (the form reads initialData once).
                key={currentSeason?.id ?? 'new'}
                initialData={currentSeason || undefined}
                onSubmit={handleSeasonSubmit}
                onCancel={handleCancel}
                loading={loading}
                isEdit={isEdit}
                existingSeasons={!isEdit ? seasons : undefined}
                leagueId={leagueId}
              />
            )}

            {activeTab === 'points' && isEdit && (
              <PointsConfigForm
                key={pointsConfig ? 'points-loaded' : 'points-empty'}
                initialData={pointsConfig || undefined}
                onSubmit={handlePointsSubmit}
                onCancel={handleCancel}
                loading={loading}
              />
            )}

            {activeTab === 'subscribers' && isEdit && (
              <div>
                {loading && (
                  <div className="flex items-center justify-center py-8">
                    <svg
                      className="animate-spin h-6 w-6 text-primary-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                )}
                {!loading && !seasonSubscribers && (
                  <p className="text-sm text-gray-500">No subscriber data loaded yet.</p>
                )}
                {!loading && seasonSubscribers && (
                  <div className="space-y-6">
                    {Object.values(Category).map(cat => {
                      const subs = seasonSubscribers.byCategory[cat];
                      if (!subs || subs.length === 0) return null;
                      return (
                        <div key={cat}>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{cat}</h4>
                          <div className="bg-gray-50 rounded-md overflow-hidden">
                            <table className="min-w-full text-sm">
                              <thead className="text-gray-500 bg-gray-100">
                                <tr>
                                  <th className="text-left px-4 py-2 font-medium">Team</th>
                                  <th className="text-left px-4 py-2 font-medium">Players</th>
                                  <th className="text-left px-4 py-2 font-medium">Division</th>
                                  <th className="text-left px-4 py-2 font-medium">Status</th>
                                  <th className="text-left px-4 py-2 font-medium">Recommended</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {subs.map(sub => (
                                  <tr key={sub.teamId}>
                                    <td className="px-4 py-2 font-medium text-gray-900">
                                      {sub.teamName}
                                    </td>
                                    <td className="px-4 py-2 text-gray-600">
                                      {sub.players.map(p => p.name).join(', ')}
                                    </td>
                                    <td className="px-4 py-2 text-gray-600">
                                      {sub.divisionName ?? (
                                        <span className="text-gray-400 italic">Unassigned</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                          sub.status === 'assigned'
                                            ? 'bg-green-100 text-green-800'
                                            : sub.status === 'pending'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-blue-100 text-blue-800'
                                        }`}
                                      >
                                        {sub.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2">
                                      {sub.movement && sub.recommendedDivisionName && (
                                        <span
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                            sub.movement === 'promoted'
                                              ? 'bg-green-100 text-green-800'
                                              : sub.movement === 'relegated'
                                              ? 'bg-red-100 text-red-800'
                                              : 'bg-gray-100 text-gray-700'
                                          }`}
                                          title={`Recommended: ${sub.recommendedDivisionName}`}
                                        >
                                          {sub.movement === 'promoted'
                                            ? '↑ promoted'
                                            : sub.movement === 'relegated'
                                            ? '↓ relegated'
                                            : '→ stays'}{' '}
                                          &rarr; {sub.recommendedDivisionName}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(seasonSubscribers.byCategory).length === 0 && (
                      <p className="text-sm text-gray-500">
                        No subscribed players for this season yet.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSeasonPage;
